import React, { useEffect, useMemo, useState } from "react";
import SidebarLayout from "../../components/SidebarLayout";
import Modal from "../../components/Modal";
import { BACKEND_URL } from "../../config";
import { Medicine } from "./types";

const hasMissingBuyingPrice = (medicine: Medicine) => {
  const type = String(medicine.type || "").toUpperCase();
  return type !== "SERVICE" && type !== "LAB" && Number(medicine.buyingPrice || 0) <= 0;
};

const MedicineBuyingPrice: React.FC = () => {
  const [items, setItems] = useState<Medicine[]>([]);
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState({ open: false, title: "", message: "" });

  const loadItems = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/medicines`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error(await response.text());
      const data = await response.json();
      const missing = (data || []).filter(hasMissingBuyingPrice);
      setItems(missing);
      setPrices(
        missing.reduce((next: Record<string, string>, medicine: Medicine) => {
          next[medicine.id] = medicine.buyingPrice ? String(medicine.buyingPrice) : "";
          return next;
        }, {})
      );
    } catch (err: any) {
      setModal({ open: true, title: "Unable to load medicines", message: err.message || "Please try again." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return items;
    return items.filter((item) =>
      [item.name, item.brand, item.manufacturer, item.batchNo]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [items, search]);

  const savePrice = async (medicine: Medicine) => {
    const buyingPrice = Number(prices[medicine.id] || 0);
    if (!buyingPrice || buyingPrice <= 0) {
      setModal({ open: true, title: "Buying price required", message: "Enter a buying price greater than zero." });
      return;
    }

    setSavingId(medicine.id);
    try {
      const response = await fetch(`${BACKEND_URL}/api/medicines/${medicine.id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ buyingPrice }),
      });
      if (!response.ok) throw new Error(await response.text());
      setItems((prev) => prev.filter((item) => item.id !== medicine.id));
      setModal({ open: true, title: "Buying price updated", message: `${medicine.name} has been updated.` });
    } catch (err: any) {
      setModal({ open: true, title: "Unable to save buying price", message: err.message || "Please try again." });
    } finally {
      setSavingId(null);
    }
  };

  return (
    <SidebarLayout title="Medicine Buying Price" subtitle="Admin-only list of medicines that still need buying price.">
      <Modal open={modal.open} title={modal.title} message={modal.message} onClose={() => setModal((prev) => ({ ...prev, open: false }))} />

      <section className="rounded border border-border-light bg-surface-light p-4">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded border border-border-light p-2 sm:max-w-md"
            placeholder="Search medicine, brand, supplier, batch"
          />
          <div className="text-sm font-semibold text-subtext-light">{filteredItems.length} pending</div>
        </div>

        {loading ? (
          <div className="p-4 text-sm text-subtext-light">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] table-fixed text-left text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wide text-subtext-light">
                  <th className="w-56 p-2">Medicine</th>
                  <th className="w-32 p-2">Brand</th>
                  <th className="w-36 p-2">Supplier</th>
                  <th className="w-28 p-2">Batch</th>
                  <th className="w-20 p-2">Stock</th>
                  <th className="w-24 p-2">MRP</th>
                  <th className="w-28 p-2">Buying Price</th>
                  <th className="w-24 p-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <tr key={item.id} className="border-t border-border-light">
                    <td className="truncate p-2 font-semibold" title={item.name}>{item.name}</td>
                    <td className="truncate p-2" title={item.brand || ""}>{item.brand || "-"}</td>
                    <td className="truncate p-2" title={item.manufacturer || ""}>{item.manufacturer || "-"}</td>
                    <td className="truncate p-2" title={item.batchNo || ""}>{item.batchNo || "-"}</td>
                    <td className="p-2">{item.quantity ?? 0}</td>
                    <td className="p-2">Rs {Number(item.mrpPrice || 0).toFixed(2)}</td>
                    <td className="p-2">
                      <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={prices[item.id] || ""}
                        onChange={(e) => setPrices((prev) => ({ ...prev, [item.id]: e.target.value }))}
                        className="w-32 rounded border border-border-light p-2"
                      />
                    </td>
                    <td className="p-2">
                      <button
                        type="button"
                        onClick={() => savePrice(item)}
                        disabled={savingId === item.id}
                        className="rounded bg-primary px-3 py-2 font-semibold text-white disabled:opacity-50"
                      >
                        {savingId === item.id ? "Saving..." : "Save"}
                      </button>
                    </td>
                  </tr>
                ))}
                {!filteredItems.length && (
                  <tr>
                    <td colSpan={8} className="p-4 text-center text-subtext-light">
                      No medicines pending buying price.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </SidebarLayout>
  );
};

export default MedicineBuyingPrice;
